var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { assertUserType } from "healthex-common/checkusertype";
import { StudyUserDto } from "healthex-common/study.dtos";
import { UserPermissions, UserType } from "healthex-common/user.dtos";
import { getFullName, indexBy } from "healthex-common/utils";
import { computed, observable } from "mobx";
import { FormModel } from "./FormModel";
import { SimpleDialogModel } from "./SimpleDialogModel";
export function addedUserDisplayName(user, nameOrEmailPrefix) {
    var _a, _b;
    const prefixLower = nameOrEmailPrefix.toLowerCase();
    if (((_a = user.firstName) === null || _a === void 0 ? void 0 : _a.toLowerCase().startsWith(prefixLower)) || ((_b = user.lastName) === null || _b === void 0 ? void 0 : _b.toLowerCase().startsWith(prefixLower))) {
        return getFullName(user);
    }
    return user.email;
}
export function usersMissingResearcherPermission(users) {
    return users.filter((userToAdd) => !userToAdd.user.permissions.includes(UserPermissions.RESEARCHER));
}
export var StudyUsersFormViewType;
(function (StudyUsersFormViewType) {
    StudyUsersFormViewType["REQUIREMENTS"] = "REQUIREMENTS";
})(StudyUsersFormViewType || (StudyUsersFormViewType = {}));
export class StudyUsersFormModel {
    constructor(opts) {
        var _a, _b, _c, _d;
        this.addUsersFormModel = new FormModel({
            users: [],
        }, []);
        this.addUsersErrors = new Set();
        this.addUserErrorMessage = null;
        this.currentUsers = [];
        this.confirmRemoveDialog = new SimpleDialogModel();
        this.confirmRemoveUser = null;
        this.provisioningService = opts.container.provisioningService;
        this.authManager = opts.container.authManager;
        this.existingUsersMyOrg = opts.existingUsers;
        this.organizationsManager = opts.container.organizationsManager;
        this.onUpdate = opts.onUpdate;
        this.viewType = opts.viewType;
        const removedSet = new Set((_b = (_a = opts.currentUpdate) === null || _a === void 0 ? void 0 : _a.removed.map((removed) => removed.user.id)) !== null && _b !== void 0 ? _b : []);
        this.currentUsers = [...this.existingUsersMyOrg, ...((_d = (_c = opts.currentUpdate) === null || _c === void 0 ? void 0 : _c.updated) !== null && _d !== void 0 ? _d : [])].filter((user) => !removedSet.has(user.user.id));
    }
    get isResearcher() {
        return this.authManager.isResearcher;
    }
    async getMatchingUser(nameOrEmailPrefix) {
        if (!this.authManager.orgId)
            return [];
        const { results } = await this.provisioningService.getUsersByOrganization(this.authManager.orgId, nameOrEmailPrefix);
        return results
            .map((user) => {
            const validUser = assertUserType(user, UserType.HEALTHCARE_ORGANIZATION, UserType.EXTERNAL_RESEARCHER);
            return {
                user: validUser,
                display: addedUserDisplayName(validUser, nameOrEmailPrefix),
            };
        })
            .sort((a, b) => a.display.length - b.display.length);
    }
    get addButtonEnabled() {
        return this.addUsersFormModel.input.users.length > 0;
    }
    addSelectedUsers() {
        this.addUsersErrors.clear();
        this.addUserErrorMessage = null;
        const invalidRoles = usersMissingResearcherPermission(this.addUsersFormModel.input.users);
        if (invalidRoles.length) {
            this.addUserErrorMessage = "Please ensure the team members have Researcher permissions.";
            invalidRoles.forEach((user) => this.addUsersErrors.add(user.user.id));
            return;
        }
        this.addUsersFormModel.input.users.forEach((userToAdd) => {
            const newStudyUser = new StudyUserDto({
                user: userToAdd.user,
            });
            const existingIndex = this.currentUsers.findIndex((currentUser) => currentUser.user.id === userToAdd.user.id);
            if (existingIndex < 0) {
                this.currentUsers.push(newStudyUser);
            }
            else {
                this.currentUsers.splice(existingIndex, 1, newStudyUser);
            }
        });
        this.addUsersFormModel.updateInput({ users: [] });
        this.generateUpdateEvent();
    }
    async removeExistingUser(user) {
        this.confirmRemoveUser = user;
        if (this.viewType !== StudyUsersFormViewType.REQUIREMENTS) {
            const confirmed = await this.confirmRemoveDialog.showAndWait();
            if (!confirmed)
                return;
        }
        const existingIndex = this.currentUsers.findIndex((currentUser) => currentUser.user.id === user.user.id);
        if (existingIndex >= 0) {
            this.currentUsers.splice(existingIndex, 1);
        }
        this.generateUpdateEvent();
    }
    generateUpdateEvent() {
        const existingUsersIndex = indexBy(this.existingUsersMyOrg, (user) => user.user.id);
        const updated = this.currentUsers.filter((currentUser) => {
            const matchingExistingUser = existingUsersIndex[currentUser.user.id];
            return !matchingExistingUser;
        });
        const currentUsersSet = new Set(this.currentUsers.map((user) => user.user.id));
        const removed = this.existingUsersMyOrg.filter((existingUser) => {
            const stillPresent = currentUsersSet.has(existingUser.user.id);
            return !stillPresent;
        });
        this.onUpdate({ updated, removed });
    }
    isMe(userId) {
        return this.authManager.id === userId;
    }
    canRemoveUser(studyUser) {
        if (this.isMe(studyUser.user.id))
            return false;
        return true;
    }
    get isCurrentUserAlreadyInStudy() {
        return this.currentUsers.some((user) => this.isMe(user.user.id));
    }
    get isNextDisabled() {
        return !this.isResearcher || this.currentUsers.some((user) => this.isMe(user.user.id));
    }
    get isNextDisabledText() {
        if (!this.isResearcher) {
            return "You need to have researcher permission to add yourself to this study.";
        }
        else if (this.isCurrentUserAlreadyInStudy) {
            return "You've already added yourself to this study.";
        }
        else {
            return null;
        }
    }
}
__decorate([
    observable
], StudyUsersFormModel.prototype, "addUsersErrors", void 0);
__decorate([
    observable
], StudyUsersFormModel.prototype, "addUserErrorMessage", void 0);
__decorate([
    observable
], StudyUsersFormModel.prototype, "currentUsers", void 0);
__decorate([
    computed
], StudyUsersFormModel.prototype, "isResearcher", null);
__decorate([
    computed
], StudyUsersFormModel.prototype, "addButtonEnabled", null);
__decorate([
    computed
], StudyUsersFormModel.prototype, "isCurrentUserAlreadyInStudy", null);
__decorate([
    computed
], StudyUsersFormModel.prototype, "isNextDisabled", null);
__decorate([
    computed
], StudyUsersFormModel.prototype, "isNextDisabledText", null);
